import React, { useEffect, useState } from "react";
import { Button, Input, Popconfirm, Select, Spin } from "antd";
import { t } from "i18next";
import ApiService from "../../core/service/ApiService";

const { TextArea } = Input;

const TYPES = [
    { value: 1, label: 'Individual' },
    { value: 2, label: 'Corporate' },
];

const EMPTY = {
    id: 0,
    type: 1,
    billingAddressName: '',
    companyName: '',
    taxNumber: '',
    taxOffice: '',
    fullAddress: '',
    phoneNumber: '',
    email: '',
    cityId: null,
    districtId: null,
    isEinvoiceRegistered: false,
};

/**
 * Fatura adresleri — liste ve düzenleme aynı yerde.
 *
 * Eski ekran bunun için /app/system/billingAddress rotasına gidiyor ve hastayı `location.state`
 * ile taşıyordu; geri dönüşte randevu bağlamı kayboluyordu. Burada sayfadan hiç çıkılmıyor.
 */
export default function BillingPanel({ patientId, patient, canEdit, onChanged }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [cities, setCities] = useState([]);
    const [districts, setDistricts] = useState([]);

    const load = async () => {
        setLoading(true);
        const res = await ApiService.PatientBillingAddress.GetBillingAddressList({ patientId });
        if (res.success) setRows(res.data || []);
        setLoading(false);
    };

    useEffect(() => {
        if (!patientId) return;
        load();
        ApiService.City.GetCityList().then(res => { if (res.success) setCities(res.data || []); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId]);

    useEffect(() => {
        if (!editing?.cityId) { setDistricts([]); return; }
        ApiService.District.GetDistrictList(editing.cityId).then(res => {
            if (res.success) setDistricts(res.data || []);
        });
    }, [editing?.cityId]);

    const startNew = () => setEditing({
        ...EMPTY,
        // Bireysel fatura için kimlik ve ad hastadan gelir — her seferinde elle yazılmasın.
        taxNumber: patient?.customId || '',
        billingAddressName: `${patient?.name || ''} ${patient?.lastname || ''}`.trim(),
    });

    const set = (key, value) => setEditing(prev => ({ ...prev, [key]: value }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...editing, patientId };
            const res = editing.id
                ? await ApiService.PatientBillingAddress.EditBillingAddress(editing.id, payload)
                : await ApiService.PatientBillingAddress.CreateBillingAddress(payload);
            if (res.success) {
                setEditing(null);
                await load();
                onChanged?.();
            }
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        const res = await ApiService.PatientBillingAddress.DeleteBillingAddress(row.id);
        if (res.success) { await load(); onChanged?.(); }
    };

    if (loading && rows.length === 0) {
        return <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spin /></div>;
    }

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 11 }}>
                <span className="lbl">{t('Billing Address')}</span>
                <span style={{ flex: 1 }} />
                {canEdit && !editing && (
                    <Button size="small" type="primary" onClick={startNew}>+ {t('New')}</Button>
                )}
            </div>

            <div className="list">
                {rows.length === 0 && <div className="empty">{t('No billing address yet.')}</div>}
                {rows.map(row => (
                    <div className="li" key={row.id}>
                        <div className="main">
                            <div className="t">
                                {t(row.type === 2 ? 'Corporate' : 'Individual')} — {row.billingAddressName || row.companyName || '—'}
                            </div>
                            <div className="s">
                                {row.taxNumber && <span className="ws-mono">{row.taxNumber}</span>}
                                {row.taxOffice && <><span>·</span><span>{row.taxOffice}</span></>}
                                {row.fullAddress && <><span>·</span><span>{row.fullAddress}</span></>}
                            </div>
                        </div>
                        {canEdit && (
                            <>
                                <Button size="small" onClick={() => setEditing({ ...EMPTY, ...row })}>{t('Edit')}</Button>
                                <Popconfirm
                                title={t('Delete?')}
                                okText={t('Delete')}
                                cancelText={t('Cancel')}
                                okButtonProps={{ danger: true }}
                                onConfirm={() => remove(row)}
                            >
                                    <Button size="small" type="text" danger>✕</Button>
                                </Popconfirm>
                            </>
                        )}
                    </div>
                ))}
            </div>

            {editing && (
                <div className="inlineform">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span className="lbl">{editing.id ? t('Edit') : t('New')}</span>
                        <span style={{ flex: 1 }} />
                        <Button size="small" onClick={() => setEditing(null)}>{t('Cancel')}</Button>
                        <Button size="small" type="primary" loading={saving} onClick={save}>{t('Save')}</Button>
                    </div>

                    <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                        <div className="f">
                            <span className="lbl">{t('Type')}</span>
                            <Select
                                value={editing.type}
                                onChange={v => set('type', v)}
                                options={TYPES.map(x => ({ value: x.value, label: t(x.label) }))}
                            />
                        </div>
                        <div className="f">
                            <span className="lbl">{t('Billing Address Name')}</span>
                            <Input value={editing.billingAddressName ?? ''} onChange={e => set('billingAddressName', e.target.value)} />
                        </div>
                        {editing.type === 2 && (
                            <div className="f" style={{ gridColumn: 'span 2' }}>
                                <span className="lbl">{t('Company Name')}</span>
                                <Input value={editing.companyName ?? ''} onChange={e => set('companyName', e.target.value)} />
                            </div>
                        )}
                        <div className="f">
                            <span className="lbl">{editing.type === 2 ? t('Tax Number') : t('Identity')}</span>
                            <Input className="ws-mono" value={editing.taxNumber ?? ''} onChange={e => set('taxNumber', e.target.value)} />
                        </div>
                        <div className="f">
                            <span className="lbl">{t('Tax Office')}</span>
                            <Input value={editing.taxOffice ?? ''} onChange={e => set('taxOffice', e.target.value)} />
                        </div>
                        <div className="f">
                            <span className="lbl">{t('City')}</span>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                value={editing.cityId ?? undefined}
                                onChange={v => { set('cityId', v); set('districtId', null); }}
                                options={cities.map(c => ({ value: c.id, label: c.name }))}
                            />
                        </div>
                        <div className="f">
                            <span className="lbl">{t('District')}</span>
                            <Select
                                showSearch
                                optionFilterProp="label"
                                value={editing.districtId ?? undefined}
                                disabled={!editing.cityId}
                                onChange={v => set('districtId', v)}
                                options={districts.map(d => ({ value: d.id, label: d.name }))}
                            />
                        </div>
                        <div className="f">
                            <span className="lbl">{t('Phone')}</span>
                            <Input className="ws-mono" value={editing.phoneNumber ?? ''} onChange={e => set('phoneNumber', e.target.value)} />
                        </div>
                        <div className="f">
                            <span className="lbl">{t('Email')}</span>
                            <Input type="email" value={editing.email ?? ''} onChange={e => set('email', e.target.value)} />
                        </div>
                        <div className="f" style={{ gridColumn: 'span 2' }}>
                            <span className="lbl">{t('Full Address')}</span>
                            <TextArea rows={2} value={editing.fullAddress ?? ''} onChange={e => set('fullAddress', e.target.value)} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
